<script setup>
import { useRouter } from 'vue-router';
import { ref, onMounted, nextTick } from 'vue';
import { getLocalData, setLocalData } from '@/utils/localdata';
import { useI18n } from 'vue-i18n';
import axios from '@/plugins/axios.js';
import { useAlerts } from '@/store/alerts.js'
import Phone from '@/views/components/Phone.vue'
import KiwishLoader from '@/views/components/KiwishLoader.vue';
import Contact from '@/views/components/Contact.vue';
import PasswordToggle from '@/views/components/PasswordToggle.vue';
import { pushGTMEvent } from '@/utils/gtm';

const { locale } = useI18n();
const router = useRouter();
const loging = ref(false);
const viewContactModal = ref(false);
const showPassword = ref(false);
const emailPhoneError = ref('');
const passError = ref('');
const VUE_ASSETS_URL = process.env.VUE_APP_ASSETS_URL;
const APP_NAME = process.env.VUE_APP_NAME;
const lang = ref();
const isValidPhone = ref(true);

const userData = ref({
  loginWith: 'phone',
  email: process.env.VUE_APP_EMAIL_DEV,
  phone: process.env.VUE_APP_PHONE_DEV,
  password: process.env.VUE_APP_PASS_DEV,
  rememberme: false,
  prefix:undefined
});

onMounted(async () => {
  await setLocalData('intro', 'true');
  lang.value = await getLocalData('lang')
  pushGTMEvent('home');
});

// Función para manejar Enter/botón "Ir" de iOS
const handleEnterLogin = () => {
  // Validar que los campos estén completos
  if (!isValidPhone.value || !userData.value.password) {
    return;
  }
  login();
};

const login = () => {
  // Prevenir doble submit
  if (loging.value) return;

  loging.value = true;
  setLocalData('prefix', userData.value.prefix);

  const loginData = {
    loginWith: userData.value.loginWith,
    email: userData.value.email,
    phone: userData.value.prefix + userData.value.phone,
    password: userData.value.password,
    lang: lang.value
  };

  axios.post('/auth/login', loginData)
    .then(async r => {
      loging.value = false;
      await nextTick();
      if (r.status == 200 && r.data?.accessToken) {
        await setLocalData('authToken', JSON.stringify(r.data.accessToken));
        await setLocalData('userData', JSON.stringify(r.data.userData));
        await setLocalData('lang', r.data.userData.lang || 'es');
        router.replace('/');
      } else {
        useAlerts().alert = {visible: true, message: r.data?.message || 'register.msg.error.general', class: 'alert-error'}
      }
    }).catch(e => {
      loging.value = false;
      // Manejar errores específicos
      if (e.response && e.response.status === 401) {
        useAlerts().alert = {
          visible: true,
          message: 'login.user.incorrect',
          class: 'alert-error'
        };
      } else {
        useAlerts().alert = {
          visible: true,
          message: e.response?.data?.message || 'register.msg.error.general',
          class: 'alert-error'
        };
      }
    })
}

</script>

<template>
  <ion-page class="bg-gradient-secrets">
    <!-- Logo header -->
    <div class="auth-logo-header">
      <img :src="VUE_ASSETS_URL + 'logokw_dark.png'"
           :srcset="VUE_ASSETS_URL + 'logokw_dark.png 1x, ' + VUE_ASSETS_URL + 'logokw_dark@2.png 2x'"
           alt="Kiwish" class="auth-logo" />
    </div>

    <div class="auth-wrapper">
      <div class="auth-container">
        <!-- Formulario -->
        <div class="auth-form">
          <div v-if="userData.loginWith == 'phone'">
            <div class="auth-input">
              <Phone v-model:phone="userData.phone" v-model:prefix="userData.prefix" v-model:isValidPhone="isValidPhone" />
            </div>
          </div>

          <div class="auth-input">
            <ion-input :type="showPassword ? 'text' : 'password'" v-model="userData.password"
              @keydown="passError = ''"
              @keydown.enter.prevent="handleEnterLogin"
              :placeholder="$t('password')"
              enterkeyhint="go"
            />
            <PasswordToggle :visible="showPassword" @toggle="showPassword = !showPassword" />
          </div>
          <ion-text class="error-text" v-if="passError != ''">{{ $t(passError) }}</ion-text>

          <div class="checkbox-row">
            <ion-checkbox v-model="userData.rememberme"></ion-checkbox>
            <ion-text class="checkbox-label">{{ $t('login.rememberme') }}</ion-text>
          </div>

          <div class="auth-links">
            <ion-text class="link-text">{{ $t('hasnot.account') }}</ion-text>
            <ion-text @click="router.replace('/auth/register')" class="link-action">{{ $t('register') }}</ion-text>
          </div>

          <button class="btn-primary" :disabled="!isValidPhone || !userData.password" @click="login();">
            {{ $t('login') }}
          </button>

          <ion-text @click="router.replace('/auth/recoverpass')" class="link-forgot">{{ $t('login.forgot_password') }}</ion-text>

          <div class="contact-link-container">
            <ion-text @click="router.push('/app/help')" class="link-contact">{{ $t('contact.link') }}</ion-text>
          </div>
        </div>
      </div>
    </div>

    <!-- MODAL CONTACT -->
    <div v-if="viewContactModal" class="overlay-modal">
      <Contact @close="viewContactModal = false" />
    </div>

    <!-- Loader fuera del content para cubrir toda la pantalla -->
    <KiwishLoader :is-open="loging" :message="$t('login.loging')" />

      </ion-page>
</template>

<style scoped>
ion-page {
  --background: transparent;
}

/* Logo header */
.auth-logo-header {
  display: flex;
  justify-content: center;
  padding-top: calc(env(safe-area-inset-top, 20px) + 20px);
  padding-bottom: 10px;
}

.auth-logo {
  height: 50px;
  width: auto;
}

.auth-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.auth-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 30px;
  padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 40px);
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

/* Inputs con nuevo estilo */
.auth-input {
  background: #EDEAD5;
  border: 1px solid #5A695B;
  border-radius: 16px;
  padding: 0 16px;
  min-height: 52px;
  display: flex;
  align-items: center;
}

.auth-input ion-input {
  --background: transparent;
  --padding-start: 0;
  --padding-end: 0;
  --placeholder-color: #FF5287;
  --placeholder-opacity: 0.6;
  --color: #5A695B;
  font-size: 1rem;
  text-align: left;
}

/* Checkbox */
.checkbox-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}

.checkbox-label {
  font-size: 0.85rem;
  color: #5A695B;
}

ion-checkbox {
  --size: 22px;
  --checkbox-background-checked: #FF5287;
  --checkbox-background: #FFFFFF;
  --border-color: #FF5287;
  --border-color-checked: #FF5287;
}

ion-checkbox::part(container) {
  border-radius: 50%;
  border: 1px solid #FF5287;
}

/* Links */
.link-forgot {
  display: block;
  text-align: center;
  color: #5A695B;
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: underline;
  margin-top: 20px;
  cursor: pointer;
}

.auth-links {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px;
  margin-top: 20px;
}

.link-text {
  font-size: 0.85rem;
  color: #5A695B;
}

.link-action {
  font-size: 0.85rem;
  font-weight: 700;
  color: #5A695B;
  text-decoration: underline;
  cursor: pointer;
}

/* Botón principal */
.btn-primary {
  background: #FF5287;
  border: none;
  border-radius: 16px;
  color: #FFFFFF;
  font-size: 1rem;
  font-weight: 600;
  padding: 15px 50px;
  cursor: pointer;
  transition: opacity 0.2s;
  width: 100%;
  margin-top: 15px;
}

.btn-primary:active {
  opacity: 0.7;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Contacto */
.contact-link-container {
  text-align: center;
  margin-top: 20px;
}

.link-contact {
  font-size: 0.85rem;
  font-weight: 700;
  color: #5A695B;
  text-decoration: underline;
  cursor: pointer;
}


/* Error */
.error-text {
  font-size: 0.75rem;
  color: #e74c3c;
  margin-top: -10px;
  margin-left: 5px;
}

/* Modal */
.overlay-modal {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: linear-gradient(to bottom, var(--ion-color-header), var(--ion-color-footer));
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>